import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Info, Sparkles, X } from "lucide-react";
import { useT } from "@/i18n";

const VARIANTS = {
  info: { Icon: Info, ring: "border-brand-teal/30", chip: "bg-brand-teal/10 text-brand-teal", btn: "bg-brand-teal hover:bg-brand-tealDark", glow: "from-brand-teal/20" },
  promo: { Icon: Sparkles, ring: "border-amber-400/40", chip: "bg-amber-400/15 text-amber-600 dark:text-amber-300", btn: "bg-amber-500 hover:bg-amber-600", glow: "from-amber-400/25" },
  warning: { Icon: AlertTriangle, ring: "border-red-400/40", chip: "bg-red-500/10 text-red-600 dark:text-red-400", btn: "bg-red-600 hover:bg-red-700", glow: "from-red-500/20" },
};

function sig(popup, lang) {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify({ v: popup.variant, u: popup.cta_url, t: popup.texts })))).slice(0, 48);
  } catch {
    return `${popup.variant}|${popup.cta_url}|${lang}`;
  }
}

export default function AnnouncementPopup({ popup }) {
  const { lang } = useT();
  const [open, setOpen] = useState(false);

  const tx = popup?.texts?.[lang] || popup?.texts?.pl || {};
  const hasContent = !!popup?.enabled && (!!(tx.title || "").trim() || !!(tx.body || "").trim());
  const key = popup ? sig(popup, lang) : "";

  useEffect(() => {
    if (!hasContent) return;
    const seen = localStorage.getItem("tb-popup-seen");
    if (seen === key) return;
    const timer = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(timer);
  }, [hasContent, key]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setOpen(false);
    try { localStorage.setItem("tb-popup-seen", key); } catch { /* ignore */ }
  };

  if (!hasContent) return null;
  const v = VARIANTS[popup.variant] || VARIANTS.info;
  const { Icon } = v;
  const ctaLabel = (tx.cta_label || "").trim();
  const ctaUrl = (popup.cta_url || "").trim();
  const external = /^https?:\/\//i.test(ctaUrl);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="announcement-popup"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
        >
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />
          <motion.div
            role="dialog" aria-modal="true" aria-label={tx.title || "Komunikat"}
            className={`relative w-full max-w-md rounded-2xl border ${v.ring} bg-card shadow-2xl overflow-hidden`}
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-48 rounded-full bg-gradient-to-b ${v.glow} to-transparent blur-2xl`} />
            <button
              type="button" data-testid="announcement-popup-close" onClick={close} aria-label="Zamknij"
              className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <X size={18} />
            </button>
            <div className="relative p-7 sm:p-8">
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${v.chip} mb-4`}>
                <Icon size={24} />
              </span>
              {tx.title && <h3 data-testid="announcement-popup-title" className="font-display font-bold text-xl sm:text-2xl leading-tight mb-2">{tx.title}</h3>}
              {tx.body && <p data-testid="announcement-popup-body" className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{tx.body}</p>}
              {ctaLabel && ctaUrl && (
                <a
                  data-testid="announcement-popup-cta" href={ctaUrl}
                  {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={close}
                  className={`mt-6 inline-flex items-center justify-center gap-2 ${v.btn} text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors`}
                >
                  {ctaLabel}
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
